/**
 * Zustand store for sidebar state.
 *
 * Manages collapsed state, width, and section expansions.
 * Persists state to localStorage.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export const SIDEBAR_DEFAULT_WIDTH = 240;
export const SIDEBAR_MIN_WIDTH = 180;
export const SIDEBAR_MAX_WIDTH = 400;
export const SIDEBAR_COLLAPSE_THRESHOLD = 100;
export const SIDEBAR_COLLAPSED_WIDTH = 48;

interface SidebarState {
  isCollapsed: boolean;
  userPreference: boolean;
  width: number;
  preferredWidth: number;
  expandedSections: Set<string>;
}

interface SidebarActions {
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  setAutoCollapsed: (collapsed: boolean) => void;
  setUserPreference: (collapsed: boolean) => void;
  setWidth: (width: number) => void;
  setPreferredWidth: (width: number) => void;
  toggleSection: (sectionKey: string) => void;
  setSectionExpanded: (sectionKey: string, expanded: boolean) => void;
}

type SidebarStore = SidebarState & SidebarActions;

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      isCollapsed: false,
      userPreference: false,
      width: SIDEBAR_DEFAULT_WIDTH,
      preferredWidth: SIDEBAR_DEFAULT_WIDTH,
      expandedSections: new Set(["admin"]),

      toggleCollapsed: () =>
        set((state) => ({
          isCollapsed: !state.isCollapsed,
          userPreference: !state.isCollapsed,
        })),

      setCollapsed: (collapsed: boolean) =>
        set({ isCollapsed: collapsed, userPreference: collapsed }),

      setAutoCollapsed: (collapsed: boolean) => set({ isCollapsed: collapsed }),

      setUserPreference: (collapsed: boolean) =>
        set({ userPreference: collapsed }),

      setWidth: (width: number) => set({ width }),

      setPreferredWidth: (width: number) =>
        set({ preferredWidth: width, width }),

      toggleSection: (sectionKey: string) =>
        set((state) => {
          const newExpanded = new Set(state.expandedSections);
          if (newExpanded.has(sectionKey)) {
            newExpanded.delete(sectionKey);
          } else {
            newExpanded.add(sectionKey);
          }
          return { expandedSections: newExpanded };
        }),

      setSectionExpanded: (sectionKey: string, expanded: boolean) =>
        set((state) => {
          const newExpanded = new Set(state.expandedSections);
          if (expanded) {
            newExpanded.add(sectionKey);
          } else {
            newExpanded.delete(sectionKey);
          }
          return { expandedSections: newExpanded };
        }),
    }),
    {
      name: "animus-sidebar",
      partialize: (state) => ({
        userPreference: state.userPreference,
        preferredWidth: state.preferredWidth,
        expandedSections: Array.from(state.expandedSections),
      }),
      merge: (persisted, current) => {
        const persistedState = persisted as {
          userPreference?: boolean;
          preferredWidth?: number;
          expandedSections?: string[];
        };
        const userPref =
          persistedState?.userPreference ?? current.userPreference;
        const preferredWidth =
          persistedState?.preferredWidth ?? current.preferredWidth;
        return {
          ...current,
          userPreference: userPref,
          isCollapsed: userPref,
          width: preferredWidth,
          preferredWidth,
          expandedSections: new Set(
            persistedState?.expandedSections ??
              Array.from(current.expandedSections)
          ),
        };
      },
    }
  )
);
